import { useAuthStore } from '@/stores/authStore'
import type { CoachProposal } from '@/types/personaCoach'

export interface CoachRequestBody {
  message: string
  session_id?: string | null
  bundle_snapshot: unknown
  findings: unknown[]
  history?: Array<{ role: 'user' | 'assistant'; content: string }>
}

export interface CoachCallbacks {
  onToken: (token: string) => void
  onProposal: (proposal: CoachProposal) => void
  onComplete: (full: string, sessionId: string | null) => void
  onError: (error: Error) => void
}

export async function streamCoach(
  personaId: string,
  body: CoachRequestBody,
  cb: CoachCallbacks,
): Promise<void> {
  const token = useAuthStore.getState().accessToken
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  }
  if (token) headers['Authorization'] = `Bearer ${token}`

  try {
    const response = await fetch(`/api/personas/${personaId}/coach`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
    })
    if (!response.ok || !response.body) {
      throw new Error(`Coach request failed: ${response.status}`)
    }
    const sessionId = response.headers.get('X-Coach-Session-Id')
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let full = ''
    let currentEvent: string | null = null

    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const raw of lines) {
        const line = raw.trim()
        if (line === '' || line.startsWith(':')) {
          currentEvent = null
          continue
        }
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim()
          continue
        }
        if (!line.startsWith('data: ')) continue
        const data = line.slice(6)
        if (data === '[DONE]') {
          cb.onComplete(full, sessionId)
          return
        }
        if (currentEvent === 'error') {
          try {
            cb.onError(new Error((JSON.parse(data) as { error?: string }).error || 'Coach error'))
          } catch {
            cb.onError(new Error('Coach error'))
          }
          currentEvent = null
          continue
        }
        if (currentEvent === 'proposal') {
          try {
            cb.onProposal(JSON.parse(data) as CoachProposal)
          } catch {
            // ignore unparseable proposal frames
          }
          currentEvent = null
          continue
        }
        try {
          const parsed = JSON.parse(data) as {
            object?: string
            choices?: Array<{ delta?: { content?: string } }>
          }
          if (parsed.object === 'chat.completion.chunk') {
            const c = parsed.choices?.[0]?.delta?.content
            if (c) {
              full += c
              cb.onToken(c)
            }
          }
        } catch {
          // ignore unparseable frames
        }
        currentEvent = null
      }
    }
    cb.onComplete(full, sessionId)
  } catch (error) {
    cb.onError(error instanceof Error ? error : new Error('Unknown Coach error'))
  }
}
